/**
 * P6 客户截图智能分配 4 步弹框
 *
 * 4 步：
 *   1. 上传截图（点击/拖拽/粘贴）
 *   2. AI 识别（OCR + DeepSeek 画像）
 *   3. 选择分配人选（Top 3 + 手选）
 *   4. 确认推送
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Input, Radio, Steps } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { $, api } from '../lib/api';
import { toast } from '../lib/toast';
import { Btn, Pill } from './ui';

// 任务 B: 统一走 toast 系统（含 250ms 同 message 去重）；message.error 是 antd
// 独立系统，与 axios interceptor 的 toast.error 不互通会造成同错误 2 toast
const message = {
  success: (m: string) => toast.success(m),
  error: (m: string | { content: string; duration?: number }) => {
    const content = typeof m === 'string' ? m : m.content;
    toast.error(content);
  },
  warning: (m: string) => toast.warn(m),
  info: (m: string) => toast.info(m),
};

interface AdAttribution {
  source_note?: string;        // 归因笔记标题
  note_author?: string;         // 笔记作者
  search_keyword?: string;      // 搜索关键词
  campaign_name?: string;       // 投放计划
  creative_name?: string;       // 创意名称
  account_name?: string;        // 投放账户
}
interface ChatAnalysis {
  summary?: string;
  pain_points?: string[];
  urgency_score?: number;
  purchase_intent_score?: number;
  recommended_demand_type?: string;
}
interface AnalysisResult {
  analysis_id: number;
  image_url: string;
  ocr_text: string;
  customer_profile: {
    nickname?: string;
    xiaohongshu_name?: string;
    xiaohongshu_id?: string;
    xiaohongshu_uid?: string;
    region?: string;
    phone?: string;
    wechat?: string;
    age_range?: string; gender?: string;
    source?: string; demand_type?: string;
  };
  advertising_attribution?: AdAttribution | null;
  chat_analysis?: ChatAnalysis;
  recommended_sales: Array<{
    user_id: number; name: string; match_score: number; reasoning: string;
  }>;
}

const SRC_LABEL: Record<string, string> = {
  xiaohongshu: '🔴 小红书', douyin: '🎵 抖音', shipinhao: '📹 视频号', baidu: '🔍 百度', other: '其它',
};
const DEMAND_LABEL: Record<string, string> = {
  work_cert: '🎯 工作考证', fulltime_shop: '🏪 全职做店铺',
  sideline_shop: '💰 副业做店铺', brand_overseas: '🌍 品牌出海',
};

export function ScreenshotAllocationModal({
  open, onClose, onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (customerId: number) => void;
}) {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [profile, setProfile] = useState<any>({});
  const [selectedSales, setSelectedSales] = useState<number | null>(null);

  // 广告归因可编辑
  const setAdAttr = (patch: Partial<AdAttribution>) => {
    if (!analysis) return;
    setAnalysis({
      ...analysis,
      advertising_attribution: { ...(analysis.advertising_attribution ?? {}), ...patch },
    });
  };
  const dropRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    // v1.2-rc2 P0-C：释放 blob URL 避免泄漏
    if (preview && preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(preview); } catch {}
    }
    setStep(0); setFile(null); setPreview(null);
    setAnalysis(null); setProfile({}); setSelectedSales(null);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  // 全局粘贴 — modal 打开期间
  // v1.5 块 3 修：useLayoutEffect 同步挂监听（modal 一打开就能粘，避免 useEffect 异步 race）
  useLayoutEffect(() => {
    if (!open) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.type.startsWith('image/')) {
          const blob = it.getAsFile();
          if (blob) {
            handleImageFile(blob);
            e.preventDefault();
            break;
          }
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [open]);

  // v1.2-rc2 P0-C：智能分配向导文件上传死锁修复
  // 原因：readAsDataURL 把文件 base64 编码到内存（1MB PNG → 1.4MB string），
  //   设置为 <img src="data:..."> 触发同步 base64 decode + 大字符串 React diff
  //   → 浏览器主线程卡 20-25s 无响应
  // 修法：用 URL.createObjectURL 创建 blob: 引用，浏览器异步流式加载，<200ms
  //   + 文件 > 8MB 拒收提示
  const handleImageFile = (f: File | Blob) => {
    const fObj = f instanceof File ? f : new File([f], `paste-${Date.now()}.png`, { type: f.type });
    if (fObj.size > 8 * 1024 * 1024) {
      message.error(`图片 ${(fObj.size / 1024 / 1024).toFixed(1)} MB 超 8MB 上限，请压缩后重试`);
      return;
    }
    // 释放上一张 blob URL（避免内存泄漏）
    if (preview && preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(preview); } catch {}
    }
    setFile(fObj);
    // 异步 blob URL：不阻塞主线程，<img> 自己流式 decode
    const blobUrl = URL.createObjectURL(fObj);
    setPreview(blobUrl);
  };

  // 上传 + 分析
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('未选图');
      const fd = new FormData();
      fd.append('file', file);
      // 直接用 axios 实例，让 browser 自动设 multipart boundary
      const upResp = await api.post('/customer-screenshot/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const up = upResp.data as { analysis_id: number; image_url: string };
      const a = await $.post<AnalysisResult>(`/customer-screenshot/${up.analysis_id}/analyze`, {});
      return a;
    },
    onSuccess: (r) => {
      setAnalysis(r);
      setProfile(r.customer_profile ?? {});
      setSelectedSales(r.recommended_sales?.[0]?.user_id ?? null);
      setStep(2);
    },
    onError: (e: any) => {
      // 优先取后端真实业务错误（含 Aliyun Code）；axios 兜底 .message
      const realMsg = e?.response?.data?.message ?? e?.message ?? '上传或分析失败';
      // 长 toast 显示完整错误，不省略
      message.error({ content: realMsg, duration: 8 });
    },
  });

  const assign = useMutation({
    mutationFn: () => $.post<{ customer_id: number }>(
      `/customer-screenshot/${analysis!.analysis_id}/assign`,
      {
        sales_user_id: selectedSales,
        customer_profile: profile,
        advertising_attribution: analysis?.advertising_attribution ?? null,
      },
    ),
    onSuccess: (r) => {
      message.success(`已分配，客户 #${r.customer_id} 已创建`);
      onDone(r.customer_id);
    },
    onError: (e: any) => message.error(e?.message ?? '分配失败'),
  });

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith('image/')) handleImageFile(f);
    if (dropRef.current) dropRef.current.style.borderColor = '';
  };

  return (
    <Modal
      title="🎯 智能分配客户"
      open={open}
      onCancel={onClose}
      width={720}
      footer={null}
    >
      <Steps current={step} size="small" style={{ marginBottom: 24 }}
             items={[
               { title: '上传截图' }, { title: 'AI 识别' },
               { title: '分配人选' }, { title: '确认推送' },
             ]} />

      {step === 0 && (
        <>
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); if (dropRef.current) dropRef.current.style.borderColor = 'var(--primary)'; }}
            onDragLeave={() => { if (dropRef.current) dropRef.current.style.borderColor = ''; }}
            onDrop={onDrop}
            onClick={() => document.getElementById('hk-screenshot-file-input')?.click()}
            style={{
              padding: 40, border: '2px dashed var(--border-emphasis)',
              borderRadius: 12, textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 150ms cubic-bezier(0.4,0,0.2,1)',
              background: preview ? 'transparent' : 'var(--subtle-bg)',
            }}
          >
            {preview ? (
              <div>
                <img src={preview} alt="截图" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8 }} />
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
                  {file?.name} · {(file?.size ?? 0) / 1024 | 0} KB
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 36 }}>📎</div>
                <div style={{ marginTop: 12, fontSize: 14, color: 'var(--text-1)' }}>
                  拖拽截图到此处，或点击选择文件
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-3)' }}>
                  也可以直接 Ctrl+V 粘贴剪贴板图片（支持 PNG / JPG / WEBP，最大 10MB）
                </div>
              </>
            )}
          </div>
          <input
            id="hk-screenshot-file-input" type="file" accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImageFile(f);
              e.target.value = '';
            }}
          />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Btn kind="default" onClick={onClose}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => upload.mutate()}
                 disabled={!file || upload.isPending}>
              {upload.isPending ? 'AI 识别中（5-10 秒）…' : '下一步：AI 识别'}
            </Btn>
          </div>
        </>
      )}

      {step === 2 && analysis && (
        <>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: '0 0 220px' }}>
              <img src={analysis.image_url} alt="截图"
                   style={{ width: '100%', borderRadius: 8, display: 'block' }}
                   onError={(e) => {
                     // v1.4 修补：签名 URL 过期或加载失败时显示占位 placeholder（避免破图）
                     const img = e.currentTarget;
                     img.onerror = null;
                     img.style.background = 'var(--bg-2)';
                     img.style.minHeight = '180px';
                     img.alt = '图片加载失败（签名 URL 可能已过期，请刷新页面）';
                     img.removeAttribute('src');
                   }} />
              <details style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
                <summary style={{ cursor: 'pointer', padding: 4 }}>▶ 原始 OCR 文本</summary>
                <pre style={{
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  maxHeight: 240, overflow: 'auto',
                  padding: 8, background: 'var(--bg)', borderRadius: 4, fontSize: 11,
                }}>{analysis.ocr_text || '(OCR 不可用)'}</pre>
              </details>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              {/* 1. 客户基础信息 */}
              <Step2Card title="📇 客户基础信息" tone="neutral">
                <KvField label="小红书名字"
                         value={profile.xiaohongshu_name ?? profile.nickname}
                         onChange={(v) => setProfile({ ...profile, xiaohongshu_name: v, nickname: v })} />
                <KvField label="小红书账号"
                         value={profile.xiaohongshu_id}
                         onChange={(v) => setProfile({ ...profile, xiaohongshu_id: v })} />
                <KvField label="地区"
                         value={profile.region}
                         onChange={(v) => setProfile({ ...profile, region: v })} />
                <KvField label="手机号" masked
                         value={profile.phone}
                         onChange={(v) => setProfile({ ...profile, phone: v })} />
                <KvField label="微信号"
                         value={profile.wechat}
                         onChange={(v) => setProfile({ ...profile, wechat: v })} />
              </Step2Card>

              {/* 2. 广告归因（紫色卡） */}
              <Step2Card title="🎯 广告归因" tone="biz">
                <KvField label="归因笔记" value={analysis.advertising_attribution?.source_note}
                         onChange={(v) => setAdAttr({ source_note: v })} />
                <KvField label="笔记作者" value={analysis.advertising_attribution?.note_author}
                         onChange={(v) => setAdAttr({ note_author: v })} />
                <KvField label="搜索关键词" value={analysis.advertising_attribution?.search_keyword}
                         onChange={(v) => setAdAttr({ search_keyword: v })} />
                <KvField label="投放计划" value={analysis.advertising_attribution?.campaign_name}
                         onChange={(v) => setAdAttr({ campaign_name: v })} />
                <KvField label="创意名称" value={analysis.advertising_attribution?.creative_name}
                         onChange={(v) => setAdAttr({ creative_name: v })} />
                <KvField label="投放账户" value={analysis.advertising_attribution?.account_name}
                         onChange={(v) => setAdAttr({ account_name: v })} />
              </Step2Card>

              {/* 3. AI 分析（蓝色卡） */}
              <Step2Card title="🤖 AI 分析" tone="ai">
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>对话摘要</div>
                <div style={{
                  fontSize: 13, lineHeight: 1.5, color: 'var(--text-1)', marginBottom: 10,
                  padding: 8, background: 'rgba(255,255,255,0.6)', borderRadius: 4,
                  whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto',
                }}>{analysis.chat_analysis?.summary || '(无摘要)'}</div>
                {Array.isArray(analysis.chat_analysis?.pain_points) && analysis.chat_analysis!.pain_points!.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>痛点关键词</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {analysis.chat_analysis!.pain_points!.map((p, i) => <Pill key={i} tone="default">{p}</Pill>)}
                    </div>
                  </>
                )}
                {analysis.chat_analysis?.urgency_score != null && (
                  <ScoreBar label="紧急度评分" score={analysis.chat_analysis.urgency_score} />
                )}
                {analysis.chat_analysis?.purchase_intent_score != null && (
                  <ScoreBar label="购买意愿" score={analysis.chat_analysis.purchase_intent_score} />
                )}
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>推荐需求类型</div>
                  <Radio.Group
                    value={profile.demand_type ?? analysis.chat_analysis?.recommended_demand_type ?? ''}
                    onChange={(e) => setProfile({ ...profile, demand_type: e.target.value })}>
                    {Object.entries(DEMAND_LABEL).map(([v, l]) => <Radio key={v} value={v}>{l}</Radio>)}
                  </Radio.Group>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>来源平台</div>
                  <Radio.Group value={profile.source ?? 'xiaohongshu'}
                               onChange={(e) => setProfile({ ...profile, source: e.target.value })}>
                    {Object.entries(SRC_LABEL).map(([v, l]) => <Radio key={v} value={v}>{l}</Radio>)}
                  </Radio.Group>
                </div>
              </Step2Card>
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Btn kind="default" onClick={() => setStep(0)}>上一步</Btn>
            <Btn kind="primary" onClick={() => setStep(3)}>下一步：分配人选</Btn>
          </div>
        </>
      )}

      {step === 3 && analysis && (
        <Step3Allocator
          analysis={analysis}
          selectedSales={selectedSales}
          setSelectedSales={setSelectedSales}
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}

      {step === 4 && analysis && (
        <>
          <div style={{
            padding: 16, background: 'var(--primary-bg)', borderRadius: 8, marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>📋 即将创建客户：</div>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <div>客户：<strong>{profile.nickname || '未命名'}</strong></div>
              {profile.source && <div>来源：{SRC_LABEL[profile.source] ?? profile.source}</div>}
              {profile.demand_type && <div>需求：{DEMAND_LABEL[profile.demand_type] ?? profile.demand_type}</div>}
              <div>分配给：<strong>{analysis.recommended_sales.find((s) => s.user_id === selectedSales)?.name}</strong></div>
              <div>初始阶段：<strong>L0 加微信</strong></div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            确认后系统会：
            <br />· 在 customers 表创建新行（owner = 选定 sales / stage = L0）
            <br />· 给 sales 发 IM 系统通知（含 [👁️ 查看客户] [📝 立即跟进] 按钮）
            <br />· sales 持客量 +1
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Btn kind="default" onClick={() => setStep(3)}>上一步</Btn>
            <Btn kind="primary" onClick={() => assign.mutate()} disabled={assign.isPending}>
              {assign.isPending ? '创建中…' : '✓ 确认分配'}
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

/** Step 3：Top 3 AI 推荐 + 完整 sales 列表手动选 */
function Step3Allocator({ analysis, selectedSales, setSelectedSales, onBack, onNext }: any) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { data: usersResp } = useQuery<any>({
    queryKey: ['allocate-sales-list'],
    queryFn: () => $.get('/users?pageSize=200'),
  });
  // 巡检#7+8：主管也做业绩、可被分配 → 候选含 sales + manager
  const allSales = (usersResp?.items ?? usersResp ?? [])
    .filter((u: any) => ['sales', 'manager'].includes(u.role) && u.isActive);
  const recommendedIds = new Set(analysis.recommended_sales.map((r: any) => Number(r.user_id)));
  const filtered = allSales
    .filter((u: any) => !recommendedIds.has(Number(u.id)))
    .filter((u: any) => !query.trim() || (u.realName ?? '').includes(query) || (u.username ?? '').includes(query));
  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>
        🤖 AI 推荐 Top {analysis.recommended_sales.length}
      </div>
      {analysis.recommended_sales.map((s: any, i: number) => {
        const sel = selectedSales === s.user_id;
        return (
          <div key={s.user_id}
               onClick={() => setSelectedSales(s.user_id)}
               style={{
                 padding: 12, marginBottom: 8, borderRadius: 8,
                 border: `2px solid ${sel ? 'var(--primary)' : 'var(--border-default)'}`,
                 background: sel ? 'var(--primary-bg)' : 'transparent',
                 cursor: 'pointer',
               }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>#{i + 1}  {s.name}</span>
              <Pill tone={s.match_score >= 80 ? 'success' : s.match_score >= 60 ? 'warning' : 'default'}>
                匹配度 {s.match_score}
              </Pill>
            </div>
            {s.reasoning && (
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>{s.reasoning}</div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>或手动选择其他销售员</span>
          <button onClick={() => setShowAll((v) => !v)}
                  style={{
                    marginLeft: 'auto', fontSize: 12, padding: '2px 8px',
                    border: '1px solid var(--border)', background: '#fff',
                    borderRadius: 4, cursor: 'pointer',
                  }}>
            {showAll ? '收起' : `展开（${filtered.length} 人）`}
          </button>
        </div>
        {showAll && (
          <>
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="搜索姓名 / 工号" size="small" style={{ marginBottom: 8 }} />
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 6 }}>
              {filtered.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
                  无匹配销售员
                </div>
              )}
              {filtered.map((u: any) => {
                const sel = selectedSales === Number(u.id);
                const cap = Number(u.customerCapacity ?? 0);
                const cur = Math.max(0, Number(u.currentCustomerCount ?? 0)); // 追加B：夹底 0
                const usage = cap > 0 ? Math.round((cur / cap) * 100) : 0;
                return (
                  <div key={u.id}
                       onClick={() => setSelectedSales(Number(u.id))}
                       style={{
                         padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10,
                         cursor: 'pointer',
                         background: sel ? 'var(--primary-bg)' : 'transparent',
                         borderLeft: sel ? '3px solid var(--primary)' : '3px solid transparent',
                       }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{u.realName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        @{u.username} · 持客 {cur}/{cap} ({usage}%)
                      </div>
                    </div>
                    {sel && <span style={{ fontSize: 11, color: 'var(--primary)' }}>已选 ✓</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Btn kind="default" onClick={onBack}>上一步</Btn>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedSales && (
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              已选 <strong>{analysis.recommended_sales.find((r: any) => r.user_id === selectedSales)?.name
                ?? allSales.find((u: any) => Number(u.id) === Number(selectedSales))?.realName
                ?? `#${selectedSales}`}</strong>
            </span>
          )}
          <Btn kind="primary" onClick={onNext} disabled={!selectedSales}>下一步：确认推送</Btn>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

/* Step 2 业务版 — 3 卡 helpers */
function Step2Card({ title, tone, children }: { title: string; tone: 'neutral' | 'biz' | 'ai'; children: any }) {
  const style: React.CSSProperties = {
    padding: 12, borderRadius: 8,
    background: tone === 'biz' ? 'rgba(124,77,255,0.06)'
              : tone === 'ai' ? 'rgba(51,112,255,0.05)'
              : 'var(--bg-2, #F7F8FA)',
    border: '1px solid ' + (tone === 'biz' ? 'rgba(124,77,255,0.18)'
                          : tone === 'ai' ? 'rgba(51,112,255,0.18)'
                          : 'var(--border-default)'),
  };
  return (
    <div style={style}>
      <div style={{
        fontSize: 13, fontWeight: 500, marginBottom: 10,
        color: tone === 'biz' ? 'var(--purple-text, #5828d6)'
             : tone === 'ai' ? 'var(--primary, #3370FF)'
             : 'var(--text-1)',
      }}>{title}</div>
      {children}
    </div>
  );
}

function KvField({ label, value, onChange, masked }:
  { label: string; value?: string | null; onChange: (v: string) => void; masked?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [show, setShow] = useState(!masked);
  const display = !value ? '—'
                : masked && !show ? (value.length > 4 ? value.slice(0, 3) + '****' + value.slice(-4) : '****')
                : value;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
      <span style={{ minWidth: 90, color: 'var(--text-3)' }}>{label}</span>
      {editing ? (
        <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)}
               onBlur={() => setEditing(false)} onPressEnter={() => setEditing(false)}
               autoFocus size="small" style={{ flex: 1 }} />
      ) : (
        <span style={{ flex: 1, color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </span>
      )}
      {masked && value && (
        <button onClick={() => setShow((s) => !s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-3)' }}>
          {show ? '🙈' : '👁️'}
        </button>
      )}
      <button onClick={() => setEditing(!editing)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-3)' }}
              title="编辑">
        ✏️
      </button>
    </div>
  );
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  const dots = Array.from({ length: 10 }).map((_, i) => i < score);
  const tone = score >= 7 ? 'var(--success)' : score >= 4 ? 'var(--warning)' : 'var(--text-3)';
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {dots.map((on, i) => (
          <span key={i} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: on ? tone : 'var(--border-default)',
          }} />
        ))}
        <span style={{ marginLeft: 8, fontSize: 12, color: tone }}>{score}/10</span>
      </div>
    </div>
  );
}
